import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSoftphone } from '../contexts/SoftphoneContext';
import { useSantralGorusme } from '../contexts/SantralGorusmeContext';
import {
  santralBaslatGorusmeKaydi,
  updateGorusmeKaydi,
  otomatikSonlandirGorusmeKaydi,
} from '../services/gorusmeKaydiService';
import { searchCustomers } from '../services/customersService';
import { normalizeTurkishPhone } from '../utils/phoneNormalize';

/**
 * SIP accepted → görüşme kaydı başlat → servis kaydı oluştur sayfasına yönlendir.
 */
const SantralGorusmeBridge = () => {
  const navigate = useNavigate();
  const { setOnCallAccepted, setOnCallEnded } = useSoftphone();
  const { setSession, clearSession } = useSantralGorusme();
  const activeGorusmeRef = useRef(null);
  const processingRef = useRef(false);

  useEffect(() => {
    setOnCallAccepted(async ({ telefon, sipCallId, yon }) => {
      if (processingRef.current) return;
      processingRef.current = true;
      try {
        const res = await santralBaslatGorusmeKaydi({ telefon, sipCallId });
        const gorusmeKaydi = res?.veri?.gorusmeKaydi;
        const gorusmeKaydiId = gorusmeKaydi?.id;
        if (!gorusmeKaydiId) return;

        activeGorusmeRef.current = gorusmeKaydiId;

        const normalizedTelefon = normalizeTurkishPhone(telefon) || telefon;
        let musteri = res?.veri?.musteri;
        let musteriId = musteri?.id || gorusmeKaydi?.musteriId || undefined;
        const apiMusteriTel = normalizeTurkishPhone(musteri?.telefon || '');
        if (musteri && apiMusteriTel && apiMusteriTel !== normalizedTelefon) {
          musteri = null;
          musteriId = undefined;
        }
        if (!musteriId && normalizedTelefon) {
          try {
            const searchRes = await searchCustomers(normalizedTelefon, { aktif: true, limit: 5 });
            const list = searchRes?.veri?.musteriler || searchRes?.veri || [];
            const arr = Array.isArray(list) ? list : [];
            const found = arr.find((m) => {
              const ph = normalizeTurkishPhone(m.telefon || m.phone || '');
              return ph === normalizedTelefon;
            }) || arr[0];
            if (found) {
              const cid = found.id || found._id;
              musteriId = cid;
              musteri = {
                id: cid,
                adi: found.adi || found.name || '',
                soyadi: found.soyadi || found.surname || '',
                telefon: found.telefon || found.phone || normalizedTelefon,
                iletisimIzni: found.iletisimIzni || { verildi: false },
              };
            }
          } catch {
            /* müşteri araması başarısız */
          }
        }

        if (yon === 'gelen') {
          const initialTab = musteriId ? 'gorusme-hub' : 'customer-search';

          const selectedCustomer = musteriId && musteri ? {
            id: musteriId,
            _id: musteriId,
            adi: musteri.adi || '',
            soyadi: musteri.soyadi || '',
            name: musteri.adi || '',
            surname: musteri.soyadi || '',
            telefon: musteri.telefon || normalizedTelefon,
            phone: musteri.telefon || normalizedTelefon,
            iletisimIzni: musteri.iletisimIzni || { verildi: false },
          } : null;

          setSession({
            isSantralFlow: true,
            gorusmeKaydiId,
            musteriId: musteriId || null,
            santralTelefon: normalizedTelefon,
            activeTab: initialTab,
            maxAllowedTab: initialTab,
            selectedCustomer,
          });

          navigate('/service-records/create', {
            state: {
              fromSantral: true,
              gorusmeKaydiId,
              musteriId,
              telefon: normalizedTelefon,
              initialTab,
            },
          });
        } else if (yon === 'giden') {
          const initialTab = musteriId ? 'gorusme-hub' : 'customer-search';

          const selectedCustomer = musteriId && musteri ? {
            id: musteriId,
            _id: musteriId,
            adi: musteri.adi || '',
            soyadi: musteri.soyadi || '',
            name: musteri.adi || '',
            surname: musteri.soyadi || '',
            telefon: musteri.telefon || normalizedTelefon,
            phone: musteri.telefon || normalizedTelefon,
            iletisimIzni: musteri.iletisimIzni || { verildi: false },
          } : null;

          setSession({
            isSantralFlow: true,
            gorusmeKaydiId,
            musteriId,
            santralTelefon: normalizedTelefon,
            activeTab: initialTab,
            maxAllowedTab: initialTab,
            selectedCustomer,
          });
        }
      } catch (err) {
        console.error('[SantralGorusmeBridge] Görüşme kaydı başlatılamadı:', err);
      } finally {
        processingRef.current = false;
      }
    });

    setOnCallEnded(async () => {
      const id = activeGorusmeRef.current;
      if (!id) return;
      try {
        await otomatikSonlandirGorusmeKaydi(id);
      } catch (err) {
        try {
          await updateGorusmeKaydi(id, { cagriBitis: new Date().toISOString() });
        } catch (inner) {
          console.error('[SantralGorusmeBridge] cagriBitis güncellenemedi:', inner);
        }
        console.error('[SantralGorusmeBridge] otomatik sonlandırma hatası:', err);
      }
      activeGorusmeRef.current = null;
      clearSession();
    });

    return () => {
      setOnCallAccepted(null);
      setOnCallEnded(null);
    };
  }, [navigate, setOnCallAccepted, setOnCallEnded, setSession, clearSession]);

  return null;
};

export default SantralGorusmeBridge;
